import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Box, Button, CircularProgress, FormHelperText, Link, Tooltip } from "@mui/material";
import axios, { AxiosError } from "axios";
import { useRef, useState } from "react";
import { environment } from "../../environment";
import { trackEvent } from "../../reporting/reporting";
import { useUser } from "../../utils/tokens";
import { AnalyticsQueryFilters, buildAnalyticsParams } from "./hooks/buildAnalyticsParams";
import { buildMenuAnalyticsEventProps } from "./hooks/menuAnalyticsEvents";
import { AnalyticsDateRange, WeightUnit } from "./types";

// Beyond this the server rejects the export with a 400; we warn proactively (and
// disable the button) when the current result set is already over the cap.
const ROW_CAP = 10000;

// Show the in-progress spinner only once an export is taking a noticeable amount of
// time — fast exports just download without a flash of loading state.
const PROGRESS_DELAY_MS = 1000;

const baseUrl = `${environment.VITE_URL_SCHEME}${environment.VITE_SERVER_DOMAIN}/api/v1/analytics/csv`;

// Built from the server-resolved range (meta.range) so relative ranges like "last
// week" get real dates without re-deriving the period client-side. Matches the
// server's Content-Disposition name, which we can't read cross-origin.
const buildFilename = (tab: string, range: AnalyticsDateRange | undefined): string =>
  range ? `analytics_${tab}_${range.start}_${range.end}.csv` : `analytics_${tab}.csv`;

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Pull the API's error message out of a blob response body (the export uses
// responseType "blob", so a 400 body arrives as a Blob, not parsed JSON).
const extractErrorMessage = async (error: unknown): Promise<string | null> => {
  const data = (error as AxiosError)?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.errors?.[0] ?? null;
    } catch {
      return null;
    }
  }
  return null;
};

interface ExportButtonProps {
  tab: "menu-items" | "ingredients";
  filters: AnalyticsQueryFilters;
  totalCount: number;
  range: AnalyticsDateRange | undefined;
  unit?: WeightUnit;
  disabled?: boolean;
}

export const ExportButton = ({ tab, filters, totalCount, range, unit, disabled }: ExportButtonProps) => {
  const user = useUser();
  const [exporting, setExporting] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const progressTimer = useRef<ReturnType<typeof setTimeout>>();

  const overCap = totalCount > ROW_CAP;

  const handleExport = async () => {
    // Fire on export initiation (click), before the request — not on completion.
    trackEvent("Nutrition | Kitchen | Menu Analytics | CSV Exported", buildMenuAnalyticsEventProps(user, filters));
    setError(null);
    setExporting(true);
    progressTimer.current = setTimeout(() => setShowProgress(true), PROGRESS_DELAY_MS);
    try {
      const params = { ...buildAnalyticsParams(filters), ...(unit ? { unit } : {}) };
      const res = await axios.get(`${baseUrl}/${tab}`, { params, withCredentials: true, responseType: "blob" });
      triggerDownload(res.data as Blob, buildFilename(tab, range));
    } catch (err) {
      const message = await extractErrorMessage(err);
      setError(message ?? "Export failed. Please try again.");
    } finally {
      clearTimeout(progressTimer.current);
      setShowProgress(false);
      setExporting(false);
    }
  };

  const capMessage = `Too many rows to export (limit ${ROW_CAP.toLocaleString()}). Narrow your filters to enable export.`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
      <Tooltip title={overCap ? capMessage : ""}>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              showProgress ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />
            }
            onClick={handleExport}
            disabled={disabled || exporting || overCap}
          >
            {showProgress ? "Exporting…" : "Export CSV"}
          </Button>
        </span>
      </Tooltip>
      {overCap && !error && (
        <FormHelperText sx={{ color: "warning.main", maxWidth: 240, textAlign: "right" }}>{capMessage}</FormHelperText>
      )}
      {error && (
        <FormHelperText error sx={{ maxWidth: 240, textAlign: "right" }}>
          {error}{" "}
          <Link component="button" type="button" variant="caption" onClick={handleExport} disabled={exporting}>
            Retry
          </Link>
        </FormHelperText>
      )}
    </Box>
  );
};
